import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  PartnerGoalTarget,
  PartnerGoalTargetCreateInput,
} from "@colab/shared";

type UpsertTargetResponse = { id: string; target: PartnerGoalTarget };

// POST /api/admin/goals/targets
//
// Upsert keyed on (goal_id, partner_id, period_start, period_end) — the
// service maps the unique constraint defined in migration 0083 (partner
// goals & targets). Pessimistic; the new row carries a server-issued id +
// computed `current_value` snapshot that wouldn't survive an optimistic
// patch.
export function useUpsertGoalTarget() {
  const queryClient = useQueryClient();
  return useMutation<UpsertTargetResponse, ApiClientError, PartnerGoalTargetCreateInput>({
    mutationFn: (input) =>
      apiFetch<UpsertTargetResponse>("/admin/goals/targets", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.goals.targetsForGoal(data.target.goal_id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.goals.detail(data.target.goal_id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.goals.forPartner(data.target.partner_id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.partners.detail(data.target.partner_id),
      });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to save target");
    },
  });
}
